import {
  createInitialAppState,
  selectActiveEquation as selectEquationFromState,
} from "./app-state";
import type { AnyEquation } from "@src/features/equation-editor/equation";
import type { AppState } from "./app-state";
import type { Dimension } from "@src/math/types";
import type { EquationEditorActions } from "@src/features/equation-editor/editor-actions";
import type { EquationEvaluator } from "@src/features/equation-editor/evaluation/evaluator-contract";
import type { StoreApi } from "zustand/vanilla";
import type {
  AppDisplayActions,
  AppPlaybackActions,
  AppShareActions,
  GetAppState,
  SetAppState,
} from "./app-store-contract";
import { createEquationActions } from "./actions/equation-actions";
import { createEquationEvaluationCoordinator } from "@src/features/equation-editor/evaluation/evaluation-coordinator";
import { createPlaybackActions } from "./actions/playback-actions";
import { createShareActions } from "./actions/share-actions";
import { createStore } from "zustand/vanilla";
import { evaluateEquationInWorker } from "@src/features/equation-editor/evaluation/worker-evaluator";
import { resetPlayback } from "@src/features/playback/playback";

export type AppStoreState = AppState &
  EquationEditorActions &
  AppPlaybackActions &
  AppDisplayActions &
  AppShareActions;
export type AppStore = StoreApi<AppStoreState>;

export interface AppStoreOptions {
  readonly evaluateEquation: EquationEvaluator;
}

const defaultOptions: AppStoreOptions = {
  evaluateEquation: evaluateEquationInWorker,
};

export function createAppStore(
  overrides: Partial<AppStoreOptions> = {},
  initialState?: AppState,
): AppStore {
  const options: AppStoreOptions = {
    ...defaultOptions,
    ...overrides,
  };
  const resolvedInitialState =
    initialState ?? createInitialAppState(() => crypto.randomUUID());
  return createStore<AppStoreState>()((set, get) => {
    const setAppState: SetAppState = (update) => {
      set((state) => {
        const next = typeof update === "function" ? update(state) : update;
        return next;
      });
    };
    const getAppState: GetAppState = get;
    const evaluationCoordinator = createEquationEvaluationCoordinator(
      options.evaluateEquation,
    );
    const actions = {
      ...createEquationActions(setAppState, getAppState, evaluationCoordinator),
      ...createPlaybackActions(setAppState, getAppState),
      setDimension: (activeDimension: Dimension) => {
        setAppState((state) =>
          state.activeDimension === activeDimension
            ? state
            : {
                activeDimension,
                animation: resetPlayback(state.animation),
              },
        );
      },
      setBasisVisibility: (showBasis: boolean) => {
        setAppState({ showBasis });
      },
      setGridVisibility: (showGrid: boolean) => {
        setAppState({ showGrid });
      },
      ...createShareActions(setAppState, evaluationCoordinator),
    };
    return { ...resolvedInitialState, ...actions };
  });
}

export const selectActiveEquation = (state: AppStoreState): AnyEquation =>
  selectEquationFromState(state);
